import { Injectable, Inject, Injector, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';

import { BUILD_INFO } from 'src/environments/build-info';
import { ShoppingCartService } from '../shoppingCart.service';
import {
  VersionCheck,
  ContextoAtualizacao,
  DecisaoAtualizacao,
} from './version-check';
import { OrderFlowTraceService } from './order-flow-trace.service';

/**
 * O que provocou a checagem. Sem isto, o `sem_novidade` do boot e o do timer de
 * 30 min viram a mesma linha no Athena — e o primeiro é ordens de grandeza mais
 * frequente que o segundo, porque só ~1% das sessões chega aos 30 min.
 */
export type GatilhoVersao = 'boot' | 'foco' | 'timer' | 'navegacao';

/**
 * Fiação Angular do {@link VersionCheck}.
 *
 * A DECISÃO mora em `version-check.ts` e é pura — este serviço só junta os
 * fatos (release publicada, carrinho, rota, formulário) e executa o veredito.
 * A separação é o que permite testar todo o gate de segurança e todo o freio
 * sem TestBed, sem browser e sem relógio de verdade.
 *
 * ── Quando checar ───────────────────────────────────────────────────────────
 *
 *  - ao VOLTAR AO FOCO depois de um tempo escondida: é o caso que o contrato
 *    de frescor pede em primeiro lugar — a aba que ficou aberta a noite toda;
 *  - periodicamente enquanto visível: cobre a aba que nunca perde o foco (TV
 *    de balcão, monitor secundário);
 *  - a cada NavigationEnd, mas só para reavaliar uma atualização já ADIADA —
 *    é assim que o usuário que terminou o pedido recebe a versão nova sem
 *    esperar o próximo ciclo.
 *
 * Nenhum dos três faz requisição fora de hora: a busca do `version.json` é
 * limitada por `INTERVALO_MIN_MS`, então mesmo uma navegação frenética não vira
 * enxurrada de fetch.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  /** De quanto em quanto tempo, no máximo, buscar o `version.json`. */
  static readonly INTERVALO_MIN_MS = 30 * 60 * 1000;
  /** Escondida por mais que isto, checa ao voltar. */
  static readonly ESCONDIDA_MS = 30 * 60 * 1000;

  private readonly navegador: boolean;
  private readonly check = new VersionCheck();

  private ultimaBusca = 0;
  private escondidaDesde = 0;
  /** Release que já sabemos existir e está esperando uma janela segura. */
  private pendente: string | null = null;
  private iniciado = false;

  constructor(
    private router: Router,
    private cart: ShoppingCartService,
    private zone: NgZone,
    private injector: Injector,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    this.navegador = isPlatformBrowser(platformId);
  }

  /**
   * Liga os gatilhos. Chamado uma vez pelo `AppComponent`.
   *
   * Idempotente de propósito: um `ngOnInit` que rode duas vezes (HMR, teste,
   * re-bootstrap) não pode dobrar listener nem timer.
   */
  iniciar(): void {
    if (!this.navegador || this.iniciado) return;
    this.iniciado = true;

    // Fora da zone: um timer de 30 min dentro dela mantém o Angular acordado
    // para nada e degrada a performance da página inteira. O trabalho real
    // volta para a zone só quando há decisão a executar.
    this.zone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', () => this.aoTrocarVisibilidade());
      setInterval(() => this.verificar('timer'), VersionCheckService.INTERVALO_MIN_MS);
    });

    this.router.events.subscribe((e) => {
      // Só reavalia o que já está pendente: navegar não é motivo para ir à rede,
      // mas é a melhor oportunidade de aplicar uma atualização que estava presa
      // atrás do gate — o usuário acabou de sair do checkout.
      if (e instanceof NavigationEnd && this.pendente) this.aplicar(this.pendente, 'navegacao');
    });
  }

  private aoTrocarVisibilidade(): void {
    if (document.hidden) {
      this.escondidaDesde = Date.now();
      return;
    }
    const quanto = this.escondidaDesde ? Date.now() - this.escondidaDesde : 0;
    this.escondidaDesde = 0;
    if (quanto >= VersionCheckService.ESCONDIDA_MS) this.verificar('foco');
  }

  /**
   * Busca a release publicada e decide. Nunca lança e nunca propaga rejeição:
   * é chamado de listener e de timer, onde um erro não tem quem o pegue.
   */
  async verificar(gatilho: GatilhoVersao = 'timer'): Promise<DecisaoAtualizacao> {
    if (!this.navegador) return 'sem_versao';

    const agora = Date.now();
    if (this.ultimaBusca && agora - this.ultimaBusca < VersionCheckService.INTERVALO_MIN_MS) {
      // Já perguntamos há pouco. Se havia algo pendente, ainda vale reavaliar o
      // gate — que é de graça e pode ter aberto.
      return this.pendente ? this.aplicar(this.pendente, gatilho) : 'sem_novidade';
    }
    this.ultimaBusca = agora;

    const publicada = await this.buscarReleasePublicada();
    if (!publicada) return 'sem_versao';
    return this.aplicar(publicada, gatilho);
  }

  /** Aplica o veredito para uma release já conhecida. */
  private aplicar(publicada: string, gatilho: GatilhoVersao = 'navegacao'): DecisaoAtualizacao {
    const ctx = this.contexto();
    const decisao = this.check.decidir(BUILD_INFO.release, publicada, ctx);
    this.registrar(decisao, gatilho, publicada, ctx.temItensNoCarrinho);

    if (decisao === 'atualiza') {
      this.pendente = null;
      // De volta à zone: o reload é efeito colateral visível e precisa passar
      // pelos mesmos hooks que qualquer outra navegação.
      this.zone.run(() => this.check.recarregar(publicada));
      return decisao;
    }

    // Só o que é ADIÁVEL fica pendente. Freio e "sem novidade" não são espera:
    // insistir num freio é justamente o laço que ele existe para cortar.
    this.pendente = decisao.indexOf('adiado_') === 0 ? publicada : null;
    return decisao;
  }

  /**
   * Um evento por DECISÃO. Antes de 10/09/2026 o veredito era calculado e
   * descartado nos dois pontos de chamada, e não havia passo de telemetria
   * neste arquivo — com 4,6% da frota que boota rodando build vencida, a
   * decisão que governa a cura dessa população era invisível.
   *
   * Nunca lança e nunca atrasa: telemetria não pode custar a atualização. Pelo
   * `Injector` e não por dependência direta, para que a cadeia de DI da
   * telemetria fique fora da construção deste serviço.
   */
  private registrar(
    decisao: DecisaoAtualizacao,
    gatilho: GatilhoVersao,
    publicada: string,
    carrinho: boolean,
  ): void {
    try {
      const trace = this.injector.get(OrderFlowTraceService);
      trace.emit('ClientVersionChecked', {
        out: {
          decisao,
          gatilho,
          release_exec: BUILD_INFO.release || '',
          release_pub: publicada || '',
          carrinho,
        },
      }, 'VersionCheckService.aplicar');
    } catch (e) {
      // Sem telemetria a atualização continua acontecendo — o inverso não vale.
    }
  }

  /** Os fatos da tela neste instante. Cada leitura é defensiva. */
  private contexto(): ContextoAtualizacao {
    return {
      temItensNoCarrinho: this.carrinhoTemItens(),
      url: this.router.url || '',
      formSujo: this.temFormularioSujo(),
    };
  }

  private carrinhoTemItens(): boolean {
    try {
      // Na dúvida, ASSUMIR que tem: o erro barato é adiar a atualização; o caro
      // é recarregar por cima de um carrinho montado.
      return this.cart && typeof this.cart.hasItems === 'function'
        ? !!this.cart.hasItems()
        : true;
    } catch (e) {
      return true;
    }
  }

  /**
   * Existe formulário com alteração não salva na tela?
   *
   * Lido do DOM, e não do `FormGroup`: este serviço não tem como alcançar os
   * formulários de dezenas de páginas lazy, e o Angular já marca `ng-dirty` na
   * classe do elemento. É a mesma informação, disponível de um lugar só.
   */
  private temFormularioSujo(): boolean {
    try {
      return !!document.querySelector('form.ng-dirty, .ng-dirty.ng-touched');
    } catch (e) {
      return true;
    }
  }

  /**
   * `version.json` publicado ao lado do build.
   *
   * `cache: 'no-store'` além do `no-cache` do S3: o header cobre o cache HTTP,
   * mas não impede um service worker de responder do cache dele — e é
   * exatamente um service worker servindo conteúdo velho que esta etapa existe
   * para contornar.
   *
   * A URL é resolvida contra `document.baseURI` porque o app vive sob
   * `<base href="/app/">`: um caminho absoluto `/version.json` bateria na raiz
   * do domínio, que responde 301 para outro host.
   */
  private async buscarReleasePublicada(): Promise<string | null> {
    try {
      const url = new URL('version.json', document.baseURI).toString();
      const resp = await fetch(url, { cache: 'no-store', credentials: 'omit' });
      if (!resp || !resp.ok) return null;
      const dado = await resp.json();
      const release = dado && typeof dado.release === 'string' ? dado.release : '';
      return release || null;
    } catch (e) {
      // Offline, CORS, JSON quebrado, SPA fallback devolvendo HTML: em todos os
      // casos a resposta certa é "não sei", que o `decidir` trata como
      // 'sem_versao' e nunca como motivo para recarregar.
      return null;
    }
  }
}
